import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { 
  Sparkles, 
  Globe, 
  Bot, 
  Rocket, 
  CheckCircle2, 
  ArrowRight, 
  Users, 
  Building, 
  Calendar, 
  Clock, 
  UserPlus, 
  Shield, 
  CreditCard, 
  Briefcase 
} from "lucide-react"
import { useNavigate } from "react-router-dom"
import { Progress } from "@/components/ui/progress"
import { configToStepMap, useOnboardingConfig } from "@/components/onboarding/OnboardingSteps"
import { defaultOnboardingSteps } from "@/components/onboarding/OnboardingSteps"
import { useState } from "react"
import UpcomingEventsSection from "./UpcomingEventsSection"

interface FeatureCardProps {
  title: string
  description: string
  icon: any
  buttonText: string
  route: string
  primary?: boolean
  onClick?: () => void
}

const FeatureCard = ({ title, description, icon: Icon, buttonText, route, primary = false, onClick }: FeatureCardProps) => {
  const navigate = useNavigate()

  return (
    <Card className={`relative overflow-hidden transition-all duration-300 hover:shadow-lg 
      ${primary ? 'bg-gradient-to-br from-indigo-600 to-blue-600 text-white' : 'bg-gradient-to-br from-gray-50 to-white hover:from-gray-100 hover:to-gray-50'}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-3">
          <Icon className={`h-6 w-6 ${primary ? 'text-white' : 'text-primary'}`} />
          <CardTitle className={`text-lg ${primary ? 'text-white' : 'text-gray-800'}`}>{title}</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <p className={`text-sm mb-4 ${primary ? 'text-white/90' : 'text-gray-600'}`}>{description}</p>
        <Button 
          variant={primary ? "secondary" : "default"}
          className={`w-full ${primary ? 'bg-white text-primary hover:bg-white/90' : ''}`}
          onClick={() => {
            if (onClick) {
              onClick()
            } else {
              navigate(route)
            }
          }}
        >
          {buttonText}
        </Button>
      </CardContent>
    </Card>
  )
}

export default function NPODashboard({ profile, session }: { profile: any | null, session: any | null }) {
  const { config, isLoading: configLoading, isOnboardingCompleted } = useOnboardingConfig()
  const navigate = useNavigate()
  const [isCampaignDialogOpen, setIsCampaignDialogOpen] = useState(false)

  // Define all 6 onboarding steps for dashboard progress
  const onboardingSteps = [
    {
      id: 'ai_assistant',
      title: 'Configure AI Assistant',
      description: 'Set up your personal assistant to help manage campaigns and volunteers.',
      icon: Bot,
      buttonText: 'Configure Assistant',
      route: '/dashboard/chatbot/setup',
    },
    {
      id: 'customize_microsite',
      title: 'Customize Your Microsite',
      description: 'Get a bookable microsite with your events.',
      icon: Globe,
      buttonText: 'Customize Microsite',
      route: '/dashboard/mission-hub',
    },
    {
      id: 'explore_ai_tools',
      title: 'Explore AI Tools',
      description: 'See what else you can do with CauseConnect.',
      icon: Sparkles,
      buttonText: 'View Tools',
      route: '/dashboard/ai-tools',
    },
    {
      id: 'complete_profile',
      title: 'Complete Your Profile',
      description: 'Add organization details, logo, and contact info to complete your NPO profile.',
      icon: Users,
      buttonText: 'Complete Profile',
      route: '/dashboard/profile',
    },
    {
      id: 'add_community_event',
      title: 'Add Community Event',
      description: 'Create your first community event to engage volunteers and supporters.',
      icon: Calendar,
      buttonText: 'Add Event',
      route: '/dashboard/community-events',
    },
    {
      id: 'connect_calendar',
      title: 'Connect Your Calendar',
      description: 'Sync your events and manage schedules by connecting your calendar.',
      icon: Clock,
      buttonText: 'Connect Calendar',
      route: '/dashboard/calendar',
    },
  ];

  // Map config to completion state for each step
  const steps = onboardingSteps.map(step => ({
    ...step,
    completed: config ? config[configToStepMap[step.id]] || false : false
  }));

  const completedStepsCount = steps.filter(step => step.completed).length;
  const progressPercentage = (completedStepsCount / steps.length) * 100;

  return (
    <div className="space-y-6">
      {/* Welcome Section with Onboarding Progress */}
      <div className={`relative overflow-hidden rounded-xl ${!isOnboardingCompleted ? 'bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 p-8 text-white': ''}`}>
        <div className="relative z-10">
          <h1 className="text-4xl font-bold">Welcome back, {profile?.org_name}! 👋</h1>
          <p className="mt-2 text-white/90">Let’s get your nonprofit online and ready to engage.</p>
          
          {!isOnboardingCompleted && (<div className="mt-6 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span>Setup Progress</span>
              <span className="font-medium">{Math.round(progressPercentage)}% Complete</span>
            </div>
            <Progress value={progressPercentage} className="h-2 bg-white/20" />
          </div>
          )}
        </div>
        <div className="absolute right-0 top-0 h-full w-1/3 bg-gradient-to-l from-white/10 to-transparent transform rotate-12 translate-x-1/2" />
      </div>

      {/* Onboarding Steps - 6 actionable onboarding cards */}
      {!isOnboardingCompleted && (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {steps.map(step => (
            <FeatureCard
              key={step.id}
              title={step.title}
              description={step.description}
              icon={step.icon}
              buttonText={step.buttonText}
              route={step.route}
            />
          ))}
        </div>
      )}
      {/* End onboarding cards */}
      {/* Upcoming Events Section */}
      <UpcomingEventsSection profile={profile} session={session} />
    </div>
  )
}
